import { AssumptionSetAggregate } from '@/services/db/models/AssumptionSetAggregate'
import { VariableStateSelectEntry } from '@/services/db/schema'
import { Variable } from '@/types/variable'

import {
  ChangedVariableField,
  getChangedVariableFields,
} from './getChangedVariableFields'
import isCPTChanged from './isCPTChanged'
import { NetworkDiff } from './networkComparison'
import {
  getObservedState,
  pairOldAndNewVariables,
  pairOldAndNewAssumptions,
  pairOldAndNewAssumptionSets,
  pairOldAndNewRequirements,
} from './pairEntitiesInTwoNetwork'
import { KnownTags } from '../../general/findTagsInText'

export type ShownChangesInCompare = {
  probability: boolean
  granularity: boolean
  intervalUnit: boolean
  intervals: boolean
  transitionFunctions: boolean
  parameterizationType: boolean
  states: boolean
  optimalState: boolean
  name: boolean
  type: boolean
  comment: boolean
  tags: boolean
  definition: boolean
  references: boolean
  formula: boolean
  prevalenceExplanation: boolean
  arrowComment: boolean
  assumptionSets: boolean
}

export type NetworkToCompare = {
  variables: Array<Variable>
  variableStates: Array<VariableStateSelectEntry>
  assumptionSets?: Array<AssumptionSetAggregate>
  /**
   * The network's default continuous transition band. Optional because
   * only the transition comparison needs it; callers that omit it simply
   * get no network-level default line.
   */
  defaultTransitionHalfBandPercent?: number
}

export type CompareOptionProps = {
  filterTag?: string
  shownChanges?: ShownChangesInCompare
  commentLineSize?: number
  displayPublicationStatus?: boolean
}

export const OPTIONS_FOR_RELEASE_NOTE = {
  filterTag: KnownTags.Public as string,
  shownChanges: {
    probability: false,
    granularity: true,
    intervalUnit: true,
    intervals: true,
    // Off by default: a release note drives content/app work, and a
    // re-tuned transition band changes none of it — the states and their
    // intervals are what the consumer side reads.
    transitionFunctions: false,
    parameterizationType: false,
    states: true,
    optimalState: true,
    name: true,
    type: true,
    comment: true,
    tags: true,
    definition: true,
    references: true,
    formula: true,
    prevalenceExplanation: true,
    arrowComment: true,
    assumptionSets: false,
  },
  commentLineSize: 100,
  displayPublicationStatus: true,
}

const compareNetworks = (
  networks: {
    oldNetwork: NetworkToCompare
    newNetwork: NetworkToCompare
  },
  networkNames: {
    oldNetworkName: string
    newNetworkName: string
  },
  userOptions: CompareOptionProps,
) => {
  const diff = new NetworkDiff(networkNames)
  const filterTag =
    userOptions.filterTag?.toLowerCase() ?? OPTIONS_FOR_RELEASE_NOTE.filterTag
  const shownChanges = {
    ...OPTIONS_FOR_RELEASE_NOTE.shownChanges,
    ...userOptions.shownChanges,
  }
  const displayPublicationStatus =
    userOptions.displayPublicationStatus ??
    OPTIONS_FOR_RELEASE_NOTE.displayPublicationStatus
  const commentLineSize =
    userOptions.commentLineSize ?? OPTIONS_FOR_RELEASE_NOTE.commentLineSize

  const oldDefaultBand = networks.oldNetwork.defaultTransitionHalfBandPercent
  const newDefaultBand = networks.newNetwork.defaultTransitionHalfBandPercent
  if (
    shownChanges.transitionFunctions &&
    oldDefaultBand !== undefined &&
    newDefaultBand !== undefined &&
    oldDefaultBand !== newDefaultBand
  ) {
    diff.setChangedDefaultTransitionBand({
      from: oldDefaultBand,
      to: newDefaultBand,
    })
  }

  const variablesToCompare = pairOldAndNewVariables({
    oldVariables: networks.oldNetwork.variables,
    oldVariableStates: networks.oldNetwork.variableStates,
    newVariables: networks.newNetwork.variables,
    newVariableStates: networks.newNetwork.variableStates,
  })

  variablesToCompare.forEach(({ oldVariable, newVariable }) => {
    if (!oldVariable && !newVariable) {
      return
    }
    const oldTags = oldVariable?.tags ?? []
    const newTags = newVariable?.tags ?? []
    if (filterTag !== '') {
      const hasFilterTag =
        oldTags.includes(filterTag) || newTags.includes(filterTag)
      if (!hasFilterTag) {
        return
      }
    }
    if (displayPublicationStatus) {
      if (newVariable && newTags.includes(KnownTags.Public)) {
        if (!oldTags.includes(KnownTags.Public)) {
          diff.addNewPublishedVariable({
            type: newVariable.type.name,
            variableName: newVariable.name,
          })
        }
      }
      if (oldVariable && oldTags.includes(KnownTags.Public)) {
        if (!newVariable || !newTags.includes(KnownTags.Public)) {
          diff.addUnpublishedVariable({
            type: oldVariable.type.name,
            variableName: oldVariable.name,
          })
        }
      }
    }
    if (!oldVariable && newVariable) {
      diff.addAddedVariable({
        type: newVariable.type.name,
        variableName: newVariable.name,
      })
      return
    }
    if (oldVariable && !newVariable) {
      diff.addDeletedVariable({
        type: oldVariable.type.name,
        variableName: oldVariable.name,
      })
      return
    }
    if (oldVariable && newVariable) {
      const variableProps = {
        type: newVariable.type.name,
        variableName: newVariable.name,
      }

      // Per-field changes are enumerated by the shared
      // `getChangedVariableFields` helper so the new
      // assistant-facing structured comparison and this text
      // release-note tool stay in sync on which fields participate
      // in cross-network diffing. CPT/probability changes are not
      // emitted by the helper (the assistant tool elides them and
      // this tool guards them behind a flag that defaults off);
      // they stay handled inline below.
      for (const event of getChangedVariableFields(oldVariable, newVariable)) {
        if (!shownChanges[SHOWN_CHANGES_KEY_BY_FIELD_KIND[event.kind]]) {
          continue
        }
        dispatchVariableFieldChange({
          diff,
          event,
          variableProps,
          commentLineSize,
        })
      }

      if (shownChanges.probability && isCPTChanged(oldVariable, newVariable)) {
        diff.addProbabilitiesChanged(variableProps)
      }
    }
  })

  if (
    shownChanges.assumptionSets &&
    networks.newNetwork.assumptionSets &&
    networks.oldNetwork.assumptionSets
  ) {
    const oldAssumptionSets = networks.oldNetwork.assumptionSets
    const newAssumptionSets = networks.newNetwork.assumptionSets
    const assumptionSetsToCompare = pairOldAndNewAssumptionSets({
      oldAssumptionSets,
      newAssumptionSets,
    })
    assumptionSetsToCompare.forEach(
      ({ oldAssumptionSet, newAssumptionSet }) => {
        if (!oldAssumptionSet && !newAssumptionSet) {
          return
        }
        if (!oldAssumptionSet && newAssumptionSet) {
          diff.addAddedAssumptionSet(newAssumptionSet.name)
          return
        }
        if (oldAssumptionSet && !newAssumptionSet) {
          diff.addDeletedAssumptionSet(oldAssumptionSet.name)
          return
        }
        if (oldAssumptionSet && newAssumptionSet) {
          if (oldAssumptionSet.name !== newAssumptionSet.name) {
            diff.addChangedAssumptionSetName({
              assumptionSetName: oldAssumptionSet.name,
              from: oldAssumptionSet.name,
              to: newAssumptionSet.name,
            })
          }
          const assumptionsToCompare = pairOldAndNewAssumptions({
            oldAssumptions: oldAssumptionSet.assumptions,
            newAssumptions: newAssumptionSet.assumptions,
            oldVariables: networks.oldNetwork.variables,
            newVariables: networks.newNetwork.variables,
            oldVariableStates: networks.oldNetwork.variableStates,
            newVariableStates: networks.newNetwork.variableStates,
          })
          assumptionsToCompare.forEach(({ oldAssumption, newAssumption }) => {
            if (!oldAssumption && !newAssumption) {
              return
            }
            if (!oldAssumption && newAssumption) {
              diff.addAddedAssumptionSetAssumption({
                assumptionSetName: newAssumptionSet.name,
                state: getObservedState(
                  newAssumption.stateIndex,
                  newAssumption.variableStates,
                ),
                name: newAssumption.variableName,
              })
              return
            }
            if (oldAssumption && !newAssumption) {
              diff.addDeletedAssumptionSetAssumption({
                assumptionSetName: oldAssumptionSet.name,
                state: getObservedState(
                  oldAssumption.stateIndex,
                  oldAssumption.variableStates,
                ),
                name: oldAssumption.variableName,
              })
              return
            }
            if (oldAssumption && newAssumption) {
              const oldState = getObservedState(
                oldAssumption.stateIndex,
                oldAssumption.variableStates,
              )
              const newState = getObservedState(
                newAssumption.stateIndex,
                newAssumption.variableStates,
              )
              if (oldState !== newState) {
                diff.addChangedAssumptionSetAssumption({
                  assumptionSetName: newAssumptionSet.name,
                  name: newAssumption.variableName,
                  from: oldState,
                  to: newState,
                })
              }
            }
          })
          const requirementsToCompare = pairOldAndNewRequirements({
            oldRequirements: oldAssumptionSet.requirements,
            newRequirements: newAssumptionSet.requirements,
            oldVariables: networks.oldNetwork.variables,
            newVariables: networks.newNetwork.variables,
            oldVariableStates: networks.oldNetwork.variableStates,
            newVariableStates: networks.newNetwork.variableStates,
          })
          requirementsToCompare.forEach(
            ({ oldRequirement, newRequirement }) => {
              if (!oldRequirement && !newRequirement) {
                return
              }
              if (!oldRequirement && newRequirement) {
                diff.addAddedRequirement({
                  assumptionSetName: newAssumptionSet.name,
                  name: newRequirement.variableName,
                })
                return
              }
              if (oldRequirement && !newRequirement) {
                diff.addDeletedRequirement({
                  assumptionSetName: oldAssumptionSet.name,
                  name: oldRequirement.variableName,
                })
                return
              }
              if (oldRequirement && newRequirement) {
                const oldTolerance = oldRequirement.toleranceValue
                const newTolerance = newRequirement.toleranceValue
                if (oldTolerance !== newTolerance) {
                  diff.addChangedRequirement({
                    assumptionSetName: newAssumptionSet.name,
                    name: newRequirement.variableName,
                    from: oldTolerance?.toString() ?? 'default',
                    to: newTolerance?.toString() ?? 'default',
                    state: newRequirement.stateId,
                    label: 'TOL',
                  })
                }
                const oldExpected = oldRequirement.expectedValue
                const newExpected = newRequirement.expectedValue
                if (oldExpected !== newExpected) {
                  diff.addChangedRequirement({
                    assumptionSetName: newAssumptionSet.name,
                    name: newRequirement.variableName,
                    from: oldExpected?.toString() ?? 'default',
                    to: newExpected?.toString() ?? 'default',
                    state: newRequirement.stateId,
                    label: 'EXP',
                  })
                }
              }
            },
          )
        }
      },
    )
  }

  return diff
}

export default compareNetworks

/**
 * Map each `ChangedVariableField` kind to the matching key on
 * `ShownChangesInCompare`. This is a 1:1 mapping today — kept explicit
 * (rather than relying on `event.kind as keyof ShownChangesInCompare`)
 * so any future divergence between event kinds and shown-change keys
 * is a typed compile error rather than a silent miss.
 */
const SHOWN_CHANGES_KEY_BY_FIELD_KIND: Record<
  ChangedVariableField['kind'],
  keyof ShownChangesInCompare
> = {
  name: 'name',
  type: 'type',
  intervalUnit: 'intervalUnit',
  intervals: 'intervals',
  transitionFunctions: 'transitionFunctions',
  parameterizationType: 'parameterizationType',
  states: 'states',
  optimalState: 'optimalState',
  granularity: 'granularity',
  comment: 'comment',
  tags: 'tags',
  definition: 'definition',
  references: 'references',
  formula: 'formula',
  prevalenceExplanation: 'prevalenceExplanation',
  arrowComment: 'arrowComment',
}

/**
 * Translate one `ChangedVariableField` event into the matching
 * `NetworkDiff.addChangedX(...)` call. Kept as a thin dispatch layer
 * so the main `compareNetworks` body reads as a high-level walk
 * (paired-variable loop → enumerate changes → emit) instead of a
 * 14-arm chain of inline `if` statements.
 */
function dispatchVariableFieldChange({
  diff,
  event,
  variableProps,
  commentLineSize,
}: {
  diff: NetworkDiff
  event: ChangedVariableField
  variableProps: { type: string; variableName: string }
  commentLineSize: number
}) {
  switch (event.kind) {
    case 'name':
      diff.addChangedName({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'type':
      diff.addChangedType({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'intervalUnit':
      diff.addChangedIntervalUnit({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'intervals':
      diff.addChangedIntervals({
        ...variableProps,
        from: event.from,
        to: event.to,
        fromStates: event.fromStates,
        toStates: event.toStates,
      })
      return
    case 'transitionFunctions':
      diff.addChangedTransitionFunctions({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'parameterizationType':
      diff.addChangedParameterizationType({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'states':
      diff.addChangedStates({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'optimalState':
      diff.addChangedOptimalState({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'granularity':
      diff.addChangedGranularity({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'comment':
      diff.addChangedComment({
        ...variableProps,
        from: event.from,
        to: event.to,
        lineSize: commentLineSize,
      })
      return
    case 'tags':
      diff.addChangedTags({
        ...variableProps,
        from: event.from,
        to: event.to,
      })
      return
    case 'definition':
      diff.addChangedDefinition({
        ...variableProps,
        from: event.from,
        to: event.to,
        lineSize: commentLineSize,
      })
      return
    case 'references':
      diff.addChangedReferences({
        ...variableProps,
        from: event.from,
        to: event.to,
        lineSize: commentLineSize,
      })
      return
    case 'formula':
      diff.addChangedFormula({
        ...variableProps,
        from: event.from,
        to: event.to,
        lineSize: commentLineSize,
      })
      return
    case 'prevalenceExplanation':
      diff.addChangedPrevalenceExplanation({
        ...variableProps,
        from: event.from,
        to: event.to,
        lineSize: commentLineSize,
      })
      return
    case 'arrowComment':
      diff.addChangedArrowComment({
        ...variableProps,
        inboundVariableName: event.inboundVariableName,
        from: event.from,
        to: event.to,
        lineSize: commentLineSize,
      })
      return
  }
}
